import { useEffect, type ReactNode } from "react";
import { ChevronRight, Phone } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import BlogPostIcon from "@/components/BlogPostIcon";
import { postsNewestFirst, type BlogPost } from "@/content/blogPosts";
import { upsertCanonical, upsertLdJson, upsertMeta } from "@/lib/head";

const SITE = "https://ozmoveco.com.au";
const glass =
  "bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(26,26,26,0.06)]";

/** Renders [label](/path) inline links inside body copy. */
const RichText = ({ text }: { text: string }) => {
  const parts: ReactNode[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={`${m.index}-${m[2]}`}
        href={m[2]}
        className="font-semibold text-[#1a1a1a] underline decoration-[#FFC300] decoration-2 underline-offset-4 hover:decoration-[#1a1a1a]"
      >
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
};

const BlogPostLayout = ({ post }: { post: BlogPost }) => {
  const url = `${SITE}/blog/${post.slug}`;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = post.metaTitle;

    const cleanups = [
      upsertMeta("name", "description", post.metaDescription),
      upsertMeta("property", "og:title", post.metaTitle),
      upsertMeta("property", "og:description", post.metaDescription),
      upsertMeta("property", "og:url", url),
      upsertMeta("property", "og:type", "article"),
      upsertMeta("name", "twitter:title", post.metaTitle),
      upsertMeta("name", "twitter:description", post.metaDescription),
      upsertCanonical(url),
      upsertLdJson("ld-blogposting", {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.metaDescription,
        datePublished: post.date,
        dateModified: post.date,
        articleSection: post.category,
        inLanguage: "en-AU",
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        author: { "@type": "Organization", name: "OZ MOVE CO.", url: `${SITE}/` },
        publisher: {
          "@type": "Organization",
          name: "OZ MOVE CO.",
          url: `${SITE}/`,
        },
      }),
      upsertLdJson("ld-blog-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      }),
    ];

    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [post, url]);

  const related = postsNewestFirst.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />

      <main className="flex-1">
        {/* Article header */}
        <section className="relative overflow-hidden bg-[#FFC300]">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.6),transparent_58%)]"
            aria-hidden="true"
          />
          <div className="container relative py-14 md:py-20">
            <nav
              aria-label="Breadcrumb"
              className="mb-8 text-sm text-[#1a1a1a]/70 flex flex-wrap items-center gap-1"
            >
              <a href="/" className="hover:text-[#1a1a1a] font-semibold">Home</a>
              <ChevronRight className="w-4 h-4 opacity-50" aria-hidden="true" />
              <a href="/blog" className="hover:text-[#1a1a1a] font-semibold">Blog</a>
              <ChevronRight className="w-4 h-4 opacity-50" aria-hidden="true" />
              <span className="text-[#1a1a1a] font-bold">{post.category}</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block bg-[#1a1a1a] text-[#FFC300] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                {post.category}
              </span>
              <h1 className="mt-5 text-3xl md:text-5xl font-black text-[#1a1a1a] leading-[1.08] tracking-tight">
                {post.title}
              </h1>
              <p className="mt-5 text-base md:text-xl text-[#1a1a1a]/75 font-medium leading-relaxed">
                {post.intro}
              </p>
              <p className="mt-6 text-sm font-semibold text-[#1a1a1a]/70">
                <time dateTime={post.date}>{post.dateLabel}</time>
                <span className="mx-2 opacity-50">·</span>
                {post.readingTime}
              </p>
            </div>
          </div>
        </section>

        {/* Body */}
        <article className="py-14 md:py-20 bg-white">
          <div className="container max-w-[46rem]">
            {post.blocks.map((block, i) => {
              if (block.type === "h2")
                return (
                  <h2
                    key={i}
                    className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight mt-12 first:mt-0 mb-4"
                  >
                    {block.text}
                  </h2>
                );
              if (block.type === "h3")
                return (
                  <h3 key={i} className="text-xl font-bold text-[#1a1a1a] mt-8 mb-3">
                    {block.text}
                  </h3>
                );
              if (block.type === "p")
                return (
                  <p
                    key={i}
                    className="text-[#1a1a1a]/80 text-base md:text-lg leading-relaxed mb-5"
                  >
                    <RichText text={block.text} />
                  </p>
                );
              if (block.type === "ul")
                return (
                  <ul key={i} className="mb-6 space-y-2.5">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex gap-3 text-[#1a1a1a]/80 text-base md:text-lg leading-relaxed">
                        <span
                          className="mt-2 w-2 h-2 shrink-0 rounded-full bg-[#FFC300] ring-2 ring-[#1a1a1a]/10"
                          aria-hidden="true"
                        />
                        <span><RichText text={item} /></span>
                      </li>
                    ))}
                  </ul>
                );
              if (block.type === "ol")
                return (
                  <ol key={i} className="mb-6 space-y-3">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex gap-3 text-[#1a1a1a]/80 text-base md:text-lg leading-relaxed">
                        <span className="mt-0.5 w-7 h-7 shrink-0 rounded-full bg-[#1a1a1a] text-[#FFC300] text-sm font-black flex items-center justify-center">
                          {j + 1}
                        </span>
                        <span className="pt-0.5"><RichText text={item} /></span>
                      </li>
                    ))}
                  </ol>
                );
              return (
                <aside key={i} className={`${glass} rounded-2xl p-5 md:p-6 my-8`}>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/60 mb-2">
                    {block.title}
                  </p>
                  <p className="text-[#1a1a1a] font-semibold text-base md:text-lg leading-relaxed">
                    <RichText text={block.text} />
                  </p>
                </aside>
              );
            })}
          </div>
        </article>

        {/* Final CTA */}
        <section className="py-16 md:py-20 bg-[#1a1a1a]">
          <div className="container max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight">
              Need help with your move?
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-9 leading-relaxed">
              {post.ctaLabel} — tell us your dates and access details and we will send a clear,
              written quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="bg-[#FFC300] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:opacity-90 transition-opacity"
              >
                Get a Free Quote
              </a>
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-white/20 transition-colors"
              >
                Contact Us
              </a>
            </div>
            <a
              href="tel:1800556654"
              data-call-source="blog_article_cta"
              aria-label="Call 1800 556 654"
              className="mt-6 inline-flex items-center justify-center gap-2 text-[#FFC300] font-bold hover:opacity-80 transition-opacity"
            >
              <Phone className="w-5 h-5" /> 1800 556 654
            </a>
          </div>
        </section>

        {/* Related */}
        <section className="py-16 md:py-20 bg-[#fafafa]">
          <div className="container">
            <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight mb-8">
              Related Articles
            </h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <a
                  key={p.slug}
                  href={`/blog/${p.slug}`}
                  className={`${glass} group rounded-3xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_45px_rgba(26,26,26,0.12)]`}
                >
                  <BlogPostIcon icon={p.icon} />
                  <p className="mt-5 text-xs font-bold uppercase tracking-wider text-[#1a1a1a]/70">
                    {p.category}
                  </p>
                  <h3 className="mt-2 text-lg font-bold text-[#1a1a1a] leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-[#1a1a1a]/70 leading-relaxed flex-1">
                    {p.excerpt}
                  </p>
                  <span className="mt-5 text-sm font-bold text-[#1a1a1a] inline-flex items-center gap-1">
                    Read Article
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </span>
                </a>
              ))}
            </div>
            <a
              href="/blog"
              className="mt-10 inline-flex items-center gap-1 font-bold text-[#1a1a1a] underline decoration-[#FFC300] decoration-2 underline-offset-4"
            >
              View all moving guides
            </a>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BlogPostLayout;